export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const

export type Weekday = typeof WEEKDAYS[number]

export interface WeeklySchedule {
  type: "weekly"
  days: Weekday[]
  time: string
  timezone: string
}

export const HOURLY_INTERVALS = [1, 2, 3, 4, 6] as const

export type HourlyInterval = typeof HOURLY_INTERVALS[number]

export interface HourlySchedule {
  type: "hourly"
  days: Weekday[]
  everyHours: HourlyInterval
  start: string
  end: string
  timezone: string
}

export type RecurringSchedule = WeeklySchedule | HourlySchedule

export type ScheduledWorkflowSchedule = RecurringSchedule | null

export type ScheduledWorkflowStatus = "active" | "paused"

export type ScheduledWorkflowPausedReason =
  | { type: "manual" }
  | { type: "error", error: { type: string } }

export const SYSTEM_PAUSE_REASONS = ["credential_invalid", "pat_disabled", "repeated_failures", "over_budget"] as const

export type SystemPauseReason = typeof SYSTEM_PAUSE_REASONS[number]

export const BLOCKED_REASONS = [...SYSTEM_PAUSE_REASONS, "platform_paused"] as const

export type BlockedReason = typeof BLOCKED_REASONS[number]

export type RunTriggerType = "schedule" | "manual"

export type RunStatus = "not_started" | "running" | "finished"

export type RunOutcome = "succeeded" | "succeeded_empty" | "failed" | "stalled" | "unknown"

export const TRIGGER_ERROR_TYPES = [
  "environment_archived_error",
  "agent_archived_error",
  "environment_not_found_error",
  "vault_not_found_error",
  "vault_archived_error",
  "file_not_found_error",
  "memory_store_archived_error",
  "skill_not_found_error",
  "session_resource_not_found_error",
  "workspace_archived_error",
  "organization_disabled_error",
  "session_rate_limited_error",
  "session_creation_rejected_error",
  "unknown_error",
  "self_hosted_resources_unsupported_error",
  "mcp_egress_blocked_error",
] as const

export type TriggerErrorType = typeof TRIGGER_ERROR_TYPES[number]

export const SESSION_STOP_REASONS = ["retries_exhausted", "budget_reached", "refusal"] as const

export type SessionStopReason = typeof SESSION_STOP_REASONS[number]

export type KnownRunErrorType = TriggerErrorType | SessionStopReason | "agent_reported"

export type RunErrorType = KnownRunErrorType | (string & {})

export interface ScheduledWorkflowLastRun {
  runId: string
  status: RunStatus
  outcome: RunOutcome | null
  errorType: RunErrorType | null
  createdAt: string
}

export interface ScheduledWorkflowLatestDeliverable {
  deliverableId: string
  createdAt: string
}

export interface ScheduledWorkflowListItem {
  scheduledWorkflowId: string
  name: string
  description: string | null
  schedule: ScheduledWorkflowSchedule
  status: ScheduledWorkflowStatus
  pausedReason: ScheduledWorkflowPausedReason | null
  systemPauseReason: SystemPauseReason | null
  blockedReason: BlockedReason | null
  nextRunAt: string | null
  configVersion: number
  lastRun: ScheduledWorkflowLastRun | null
  latestDeliverable: ScheduledWorkflowLatestDeliverable | null
  emailResults: boolean
  createdAt: string
  archivedAt: string | null
}

export interface ScheduledWorkflow extends ScheduledWorkflowListItem {
  initialMessage: string
  skills: string[]
}

export interface ScheduledWorkflowsResponse {
  workflows: ScheduledWorkflowListItem[]
  nextCursor: string | null
}

export interface CreateScheduledWorkflowPayload {
  name: string
  description?: string
  initialMessage: string
  skills: string[]
  schedule?: ScheduledWorkflowSchedule
  draftId?: string
  emailResults?: boolean
}

export interface UpdateScheduledWorkflowPayload {
  configVersion: number
  name?: string
  description?: string
  initialMessage?: string
  skills?: string[]
  schedule?: ScheduledWorkflowSchedule
  emailResults?: boolean
}

export interface ScheduledWorkflowRunDeliverable {
  deliverableId: string
  fileCount: number
}

export interface ScheduledWorkflowRun {
  runId: string
  triggerType: RunTriggerType
  status: RunStatus
  outcome: RunOutcome | null
  outcomeReason: string | null
  errorType: RunErrorType | null
  sessionId: string | null
  deliverable: ScheduledWorkflowRunDeliverable | null
  retryOfRunId: string | null
  createdAt: string
  startedAt: string | null
  endedAt: string | null
}

export interface ScheduledWorkflowRunsResponse {
  runs: ScheduledWorkflowRun[]
  nextCursor: string | null
}

export interface ScheduledWorkflowDeliverableFile {
  fileId: string
  filename: string
  contentType: string
  sizeBytes: number
}

export interface ScheduledWorkflowDeliverableListItem {
  deliverableId: string
  workflowId: string
  workflowName: string
  runId: string
  title: string
  fileCount: number
  read: boolean
  createdAt: string
}

export interface ScheduledWorkflowDeliverable extends ScheduledWorkflowDeliverableListItem {
  workflowSchedule: ScheduledWorkflowSchedule
  files: ScheduledWorkflowDeliverableFile[]
}

export interface ScheduledWorkflowDeliverablesResponse {
  deliverables: ScheduledWorkflowDeliverableListItem[]
  nextCursor: string | null
}

export interface ScheduledWorkflowUnreadCountResponse {
  count: number
}

export interface ScheduledWorkflowFileUrlResponse {
  url: string
  expiresAt: number
}

export interface CursorPageParams {
  cursor?: string
  limit?: number
}

export interface ScheduledWorkflowsListParams extends CursorPageParams {
  includeArchived?: boolean
}

export interface ScheduledWorkflowRunsListParams extends CursorPageParams {
  failuresOnly?: boolean
}

export interface ScheduledWorkflowDeliverablesListParams extends CursorPageParams {
  workflowId?: string
  excludeArchived?: boolean
}
